/**
 * CO-01 — work-email hint for the PUBLIC signup form (`/signup`).
 *
 * ⚠️ THIS IS UX ONLY. THE SERVER IS THE GATE.
 * `Mowazaf.Server/Services/General/WorkEmailPolicy.cs` runs the same rules and is the one that
 * actually decides, because anything here is bypassed by POSTing to `Company/SignupFree` directly.
 * This copy exists so the user finds out while typing instead of after a round trip. The server's
 * list is configurable at runtime (`SignupPolicy:BlockedEmailDomainsCsv`), so the two can legitimately
 * drift — always let a `WORK_EMAIL_REQUIRED` response win over a local `true`.
 *
 * Scope is the public signup funnel and nothing else. Do NOT import this into employee creation,
 * invitations or login: real employees legitimately have personal addresses.
 *
 * Kept in sync by hand with `SignupPolicyDefaults.BlockedEmailDomainsCsv`. Source: the public
 * `disposable-email-domains` and `free-email-domains` catalogues, curated, then extended with the
 * GCC/MENA consumer-ISP and Arabic-webmail block those lists miss (`emirates.net.ae`,
 * `cyberia.net.sa`, `qualitynet.net`, `batelco.com.bh`, `omantel.net.om`, `qatar.net.qa`,
 * `maktoob.com`, `mohmal.com`, …) plus the South-Asian/Filipino providers common across the GCC
 * expatriate workforce.
 */

/** Marketing "contact us" page — the escape hatch shown to a refused signup. */
export const SIGNUP_CONTACT_URL = "https://mowazaf.com/contact-us/";

/**
 * Public suffixes that must never be treated as blockable. The matcher walks parent domains, so a
 * stray `co.uk` here would refuse the entire United Kingdom.
 */
const NEVER_BLOCKABLE = new Set<string>([
  "co.uk", "org.uk", "ac.uk", "gov.uk", "me.uk", "net.uk", "sch.uk", "ltd.uk", "plc.uk",
  "com.au", "net.au", "org.au", "edu.au", "gov.au", "id.au",
  "co.nz", "net.nz", "org.nz",
  "com.br", "net.br", "org.br", "com.mx", "com.ar", "com.co", "com.pe", "com.ve",
  "com.uy", "com.ec", "com.cl", "com.bo", "com.py",
  "co.in", "net.in", "org.in", "gen.in", "firm.in", "ind.in",
  "co.jp", "ne.jp", "or.jp", "ac.jp", "go.jp",
  "co.kr", "or.kr", "ne.kr",
  "com.cn", "net.cn", "org.cn", "gov.cn", "com.hk", "com.tw", "com.sg", "com.my",
  "com.ph", "com.vn", "co.th", "co.id", "or.id", "web.id",
  "com.sa", "net.sa", "org.sa", "edu.sa", "gov.sa",
  "co.ae", "net.ae", "org.ae", "ac.ae", "gov.ae", "sch.ae",
  "com.kw", "net.kw", "com.bh", "net.bh", "com.qa", "net.qa", "com.om", "net.om",
  "com.jo", "net.jo", "com.lb", "net.lb", "com.eg", "net.eg", "org.eg",
  "com.tr", "com.pk", "com.bd", "com.lk", "com.np",
  "com.ng", "co.za", "org.za", "net.za", "co.ke", "com.gh",
  "com.pt", "com.es", "com.pl", "com.ua", "com.ru", "com.de",
  "co.il", "org.il", "net.il", "ac.il",
  "com.tn", "com.ly", "com.ma", "co.ma", "net.ma", "com.dz",
]);

/** 667 consumer / free-webmail / privacy-relay / disposable domains. */
const BLOCKED_DOMAINS = new Set<string>([
  "gmail.com", "googlemail.com", "hotmail.com", "hotmail.co.uk", "hotmail.co.nz",
  "hotmail.co.jp", "hotmail.co.kr", "hotmail.co.th", "hotmail.co.id", "hotmail.co.il",
  "hotmail.co.in", "hotmail.co.za", "hotmail.ca", "hotmail.fr", "hotmail.de", "hotmail.it",
  "hotmail.es", "hotmail.be", "hotmail.nl", "hotmail.se", "hotmail.no", "hotmail.dk",
  "hotmail.fi", "hotmail.gr", "hotmail.cz", "hotmail.hu", "hotmail.pl", "hotmail.pt",
  "hotmail.ch", "hotmail.at", "hotmail.ru", "hotmail.rs", "hotmail.ro", "hotmail.sk",
  "hotmail.my", "hotmail.ph", "hotmail.sg", "hotmail.com.ar", "hotmail.com.au",
  "hotmail.com.br", "hotmail.com.hk", "hotmail.com.mx", "hotmail.com.tr", "hotmail.com.tw",
  "hotmail.com.vn", "outlook.com", "outlook.fr", "outlook.de", "outlook.es", "outlook.it",
  "outlook.jp", "outlook.kr", "outlook.dk", "outlook.cz", "outlook.hu", "outlook.ie",
  "outlook.in", "outlook.at", "outlook.be", "outlook.lv", "outlook.my", "outlook.ph",
  "outlook.pt", "outlook.sa", "outlook.sg", "outlook.sk", "outlook.co.id", "outlook.co.il",
  "outlook.co.nz", "outlook.co.th", "outlook.com.au", "outlook.com.br", "outlook.com.gr",
  "outlook.com.pe", "outlook.com.tr", "outlook.com.vn", "live.com", "live.co.uk", "live.co.za",
  "live.ca", "live.fr", "live.de", "live.it", "live.nl", "live.se", "live.no", "live.dk",
  "live.at", "live.be", "live.ie", "live.in", "live.jp", "live.cn", "live.hk", "live.ru",
  "live.cl", "live.com.au", "live.com.ar", "live.com.mx", "live.com.my", "live.com.pt",
  "live.com.sg", "msn.com", "passport.com", "windowslive.com", "hotmail.co.ve", "yahoo.com",
  "yahoo.net", "yahoo.co.uk", "yahoo.co.in", "yahoo.co.id", "yahoo.co.jp", "yahoo.co.kr",
  "yahoo.co.nz", "yahoo.co.th", "yahoo.co.za", "yahoo.ca", "yahoo.de", "yahoo.fr", "yahoo.it",
  "yahoo.es", "yahoo.gr", "yahoo.ie", "yahoo.pl", "yahoo.se", "yahoo.dk", "yahoo.no",
  "yahoo.fi", "yahoo.nl", "yahoo.be", "yahoo.at", "yahoo.ch", "yahoo.cz", "yahoo.hu",
  "yahoo.pt", "yahoo.ro", "yahoo.rs", "yahoo.com.au", "yahoo.com.ar", "yahoo.com.br",
  "yahoo.com.co", "yahoo.com.hk", "yahoo.com.mx", "yahoo.com.my", "yahoo.com.pe",
  "yahoo.com.ph", "yahoo.com.sg", "yahoo.com.tr", "yahoo.com.tw", "yahoo.com.ve",
  "yahoo.com.vn", "ymail.com", "rocketmail.com", "y7mail.com", "maktoob.com", "icloud.com",
  "me.com", "mac.com", "aol.com", "aol.co.uk", "aol.de", "aol.fr", "aim.com", "love.com",
  "games.com", "wow.com", "ygm.com", "verizon.net", "verizonmail.com", "netscape.net",
  "protonmail.com", "protonmail.ch", "proton.me", "pm.me", "zoho.com", "zohomail.com",
  "zoho.eu", "zoho.in", "gmx.com", "gmx.net", "gmx.de", "gmx.at", "gmx.ch", "gmx.co.uk",
  "gmx.fr", "gmx.us", "gmx.li", "mail.com", "email.com", "usa.com", "consultant.com",
  "europe.com", "post.com", "myself.com", "techie.com", "writeme.com", "dr.com",
  "engineer.com", "cheerful.com", "accountant.com", "linuxmail.org", "iname.com",
  "graduate.org", "doctor.com", "lawyer.com", "artlover.com", "musician.org",
  "photographer.net", "yandex.com", "yandex.ru", "yandex.by", "yandex.kz", "yandex.ua",
  "ya.ru", "mail.ru", "inbox.ru", "list.ru", "bk.ru", "internet.ru", "rambler.ru", "lenta.ru",
  "autorambler.ru", "ro.ru", "privaterelay.appleid.com", "duck.com", "simplelogin.com",
  "simplelogin.io", "simplelogin.fr", "slmail.me", "aleeas.com", "passmail.net",
  "passinbox.com", "anonaddy.com", "anonaddy.me", "addy.io", "mozmail.com",
  "relay.firefox.com", "33mail.com", "spamgourmet.com", "spamex.com", "fastmail.com",
  "fastmail.fm", "hushmail.com", "hush.com", "tutanota.com", "tutanota.de", "tutamail.com",
  "tuta.io", "keemail.me", "mailbox.org", "posteo.de", "runbox.com", "startmail.com",
  "mailfence.com", "disroot.org", "riseup.net", "cock.li", "autistici.org", "safe-mail.net",
  "countermail.com", "ctemplar.com", "hey.com", "mail.de", "inbox.lv", "inbox.eu", "seznam.cz",
  "emirates.net.ae", "eim.ae", "cyberia.net.sa", "awalnet.net.sa", "shabakah.net.sa",
  "saudi.net.sa", "qualitynet.net", "kems.net", "zajil.com", "batelco.com.bh",
  "omantel.net.om", "qatar.net.qa", "cyberia.net.lb", "inco.com.lb", "dm.net.lb",
  "terra.net.lb", "sodetel.net.lb", "idm.net.lb", "nets.jo", "go.com.jo", "index.com.jo",
  "scs-net.org", "mail.sy", "link.net", "tedata.net.eg", "soficom.com.eg", "internetegypt.com",
  "menanet.net", "menara.ma", "wanadoo.net.ma", "topnet.tn", "planet.tn", "gnet.tn",
  "hexabyte.tn", "ayna.com", "arabia.com", "araby.com", "arabtop.net", "3arabi.com",
  "rediffmail.com", "rediff.com", "sify.com", "indiatimes.com", "in.com", "vsnl.net",
  "vsnl.com", "airtelmail.com", "bsnl.in", "qq.com", "vip.qq.com", "foxmail.com", "163.com",
  "126.com", "yeah.net", "sina.com", "sina.cn", "sohu.com", "aliyun.com", "21cn.com",
  "tom.com", "139.com", "189.cn", "wo.cn", "naver.com", "daum.net", "hanmail.net", "nate.com",
  "korea.com", "docomo.ne.jp", "ezweb.ne.jp", "softbank.ne.jp", "nifty.com", "excite.co.jp",
  "goo.ne.jp", "biglobe.ne.jp", "ocn.ne.jp", "pldtdsl.net", "edsamail.com.ph", "mail.kz",
  "walla.com", "walla.co.il", "web.de", "t-online.de", "freenet.de", "arcor.de", "online.de",
  "orange.fr", "wanadoo.fr", "free.fr", "sfr.fr", "laposte.net", "neuf.fr", "bbox.fr",
  "numericable.fr", "aliceadsl.fr", "club-internet.fr", "voila.fr", "libero.it", "virgilio.it",
  "tiscali.it", "alice.it", "tin.it", "inwind.it", "email.it", "fastwebnet.it", "poste.it",
  "terra.es", "telefonica.net", "ono.com", "wanadoo.es", "ziggo.nl", "kpnmail.nl", "home.nl",
  "planet.nl", "telfort.nl", "telenet.be", "skynet.be", "scarlet.be", "btinternet.com",
  "sky.com", "virginmedia.com", "talktalk.net", "ntlworld.com", "blueyonder.co.uk",
  "tiscali.co.uk", "o2.co.uk", "orange.net", "supanet.com", "telia.com", "bredband.net",
  "spray.se", "comhem.se", "online.no", "start.no", "luukku.com", "suomi24.fi", "elisanet.fi",
  "mail.dk", "stofanet.dk", "wp.pl", "o2.pl", "interia.pl", "onet.pl", "gazeta.pl", "op.pl",
  "tlen.pl", "poczta.onet.pl", "centrum.cz", "email.cz", "volny.cz", "atlas.cz", "freemail.hu",
  "citromail.hu", "vipmail.hu", "in.gr", "otenet.gr", "mail.gr", "mynet.com",
  "superonline.com", "ttmail.com", "sapo.pt", "clix.pt", "netcabo.pt", "abv.bg", "mail.bg",
  "dir.bg", "ukr.net", "i.ua", "meta.ua", "bigmir.net", "tut.by", "comcast.net", "att.net",
  "sbcglobal.net", "bellsouth.net", "cox.net", "charter.net", "earthlink.net", "juno.com",
  "netzero.net", "roadrunner.com", "rr.com", "optonline.net", "frontier.com", "windstream.net",
  "centurylink.net", "mindspring.com", "prodigy.net", "ameritech.net", "pacbell.net",
  "swbell.net", "peoplepc.com", "excite.com", "lycos.com", "angelfire.com", "care2.com",
  "sympatico.ca", "rogers.com", "shaw.ca", "telus.net", "videotron.ca", "uol.com.br",
  "bol.com.br", "terra.com.br", "ig.com.br", "globo.com", "globomail.com", "oi.com.br",
  "r7.com", "zipmail.com.br", "click21.com.br", "prodigy.net.mx", "terra.com.mx", "terra.com",
  "fibertel.com.ar", "speedy.com.ar", "bigpond.com", "bigpond.net.au", "optusnet.com.au",
  "iinet.net.au", "tpg.com.au", "internode.on.net", "ozemail.com.au", "xtra.co.nz",
  "clear.net.nz", "webmail.co.za", "mweb.co.za", "telkomsa.net", "vodamail.co.za",
  "yahoo.com.ng", "myway.com", "mailinator.com", "mailinator.net", "mailinator2.com",
  "sogetthis.com", "spamherelots.com", "suremail.info", "binkmail.com", "bobmail.info",
  "chammy.info", "devnullmail.com", "letthemeatspam.com", "mailin8r.com", "notmailinator.com",
  "reallymymail.com", "reconmail.com", "safetymail.info", "thisisnotmyrealemail.com",
  "tradermail.info", "veryrealemail.com", "zippymail.info", "guerrillamail.com",
  "guerrillamail.net", "guerrillamail.org", "guerrillamail.biz", "guerrillamail.de",
  "guerrillamailblock.com", "grr.la", "sharklasers.com", "spam4.me", "pokemail.net",
  "10minutemail.com", "10minutemail.net", "10minutemail.org", "20minutemail.com",
  "30minutemail.com", "minuteinbox.com", "10mail.org", "10minemail.com", "temp-mail.org",
  "temp-mail.io", "temp-mail.ru", "tempmail.com", "tempmail.net", "tempmail.dev",
  "tempmailo.com", "tempmail.plus", "tempail.com", "tempr.email", "tmpmail.org", "tmpmail.net",
  "tmail.ws", "tempinbox.com", "tempmailaddress.com", "mailtemp.info", "mail-temporaire.fr",
  "temporary-mail.net", "throwawaymail.com", "throwaway.email", "trashmail.com",
  "trashmail.de", "trashmail.net", "trash-mail.com", "mytrashmail.com", "mailmetrash.com",
  "wegwerfmail.de", "wegwerfmail.net", "wegwerfmail.org", "byom.de", "yopmail.com",
  "yopmail.fr", "yopmail.net", "cool.fr.nf", "jetable.fr.nf", "nospam.ze.tc", "nomail.xl.cx",
  "mega.zik.dj", "speed.1s.fr", "courriel.fr.nf", "moncourrier.fr.nf", "monemail.fr.nf",
  "monmail.fr.nf", "jetable.org", "getnada.com", "nada.email", "nada.ltd", "inboxbear.com",
  "getairmail.com", "mohmal.com", "mohmal.in", "mohmal.im", "moakt.com", "moakt.ws",
  "moakt.co", "1secmail.com", "1secmail.net", "1secmail.org", "esiix.com", "wwjmp.com",
  "xojxe.com", "yoggm.com", "maildrop.cc", "mailnesia.com", "mailcatch.com", "mailsac.com",
  "mailtothis.com", "mytemp.email", "mailexpire.com", "spambox.us", "spambog.com",
  "spambog.ru", "spam.la", "incognitomail.com", "dispostable.com", "discard.email",
  "discardmail.com", "discardmail.de", "emailondeck.com", "emailfake.com", "fakeinbox.com",
  "fakemail.net", "dropmail.me", "emltmp.com", "mail.tm", "linshiyouxiang.net",
  "burnermail.io", "mailpoof.com", "harakirimail.com", "trbvm.com", "tafmail.com", "sute.jp",
  "vomoto.com", "luxusmail.org", "mailgenerator.net", "nowmymail.com", "mailforspam.com",
  "mailde.info", "anonbox.net", "deadaddress.com", "e4ward.com", "filzmail.com",
  "getonemail.com", "meltmail.com", "mintemail.com", "mt2015.com", "no-spam.ws",
  "objectmail.com", "proxymail.eu", "rcpt.at", "sendspamhere.com", "shieldedmail.com",
  "spamavert.com", "spamday.com", "spamfree24.org", "spaml.com", "tempemail.net",
  "tempomail.fr", "thankyou2010.com", "trickmail.net", "willselfdestruct.com", "zoemail.com",
  "armyspy.com", "cuvox.de", "dayrep.com", "einrot.com", "fleckens.hu", "gustr.com",
  "jourrapide.com", "rhyta.com", "superrito.com", "teleworm.us"
]);

/** Tail-matched families that mint arbitrary hostnames rather than a fixed set of domains. */
const BLOCKED_SUFFIXES: readonly string[] = [
  ".temporary.email", ".tempmail.dev", ".tempmail.plus", ".temp-mail.org", ".mailinator.com",
  ".yopmail.com", ".dropmail.me", ".1secmail.com", ".moakt.co", ".mohmal.com",
  ".privaterelay.appleid.com", ".mozmail.com", ".simplelogin.com", ".anonaddy.com",
  ".anonaddy.me", ".addy.io", ".duck.com", ".33mail.com", ".e4ward.com", ".spamgourmet.com",
  ".throwaway.email"
];

/**
 * Normalises a raw address to the domain that would actually route, or null when there isn't one.
 *
 * - takes the substring after the **last** `@`, so `user@gmail.com@acme.com` resolves to `acme.com`
 * - discards the local part entirely, which is why `user+tag@gmail.com` is judged on `gmail.com`
 * - lower-cases, drops trailing dots, rejects IP literals and bare hosts
 */
export function extractEmailDomain(email: string | null | undefined): string | null {
  if (!email) return null;
  const trimmed = email.trim();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;

  const domain = trimmed
    .slice(at + 1)
    .trim()
    .replace(/\.+$/, "")
    .toLowerCase();

  if (!domain || domain.length > 253) return null;
  if (domain.startsWith("[")) return null;
  if (!domain.includes(".") || domain.includes("..")) return null;
  if (domain.startsWith(".") || domain.startsWith("-")) return null;
  if (!/^[a-z0-9.-]+$/.test(domain)) return null;

  return domain;
}

/** The domain, then each parent, stopping before a bare TLD or a public suffix. */
function domainAndParents(domain: string): string[] {
  const out: string[] = [];
  let candidate = domain;
  for (;;) {
    out.push(candidate);
    const dot = candidate.indexOf(".");
    if (dot < 0) break;
    const parent = candidate.slice(dot + 1);
    if (!parent.includes(".") || NEVER_BLOCKABLE.has(parent)) break;
    candidate = parent;
  }
  return out;
}

/**
 * `true` when the address looks like a company address. A malformed address returns `true` — shape
 * validation is `validateEmail`'s job, and this must not double-report the same field.
 *
 * Sub-domains are covered by walking parents, so `x@mail.gmail.com` is `false` while
 * `x@gmail.acme.com` is `true`.
 */
export function isLikelyWorkEmail(email: string | null | undefined): boolean {
  const domain = extractEmailDomain(email);
  if (!domain) return true;

  if (BLOCKED_SUFFIXES.some((suffix) => domain.endsWith(suffix))) return false;
  return !domainAndParents(domain).some((candidate) => BLOCKED_DOMAINS.has(candidate));
}
